import { Languages } from 'lucide-react';
import { Dropdown } from 'pixelize-design-library';

import { useAppTheme } from '@core/Theme/useAppTheme';
import { settings } from '@core/store/Settings/settingsSlice';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';

import { useLanguageLoadPending } from './languageLoadStatus';
import { SUPPORTED_LANGUAGES, isSupportedLanguage } from './languages';
import { surface } from '@core/Theme/tokens';

interface LanguageSelectProps {
  size?: 'xs' | 'sm' | 'md';
}

export function LanguageSelect({ size = 'sm' }: LanguageSelectProps) {
  const dispatch = useAppDispatch();
  const language = useAppSelector((s) => s.settings.language);
  const { colors } = useAppTheme();
  const isPending = useLanguageLoadPending();

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === language) ?? SUPPORTED_LANGUAGES[0];

  return (
    <Dropdown
      options={SUPPORTED_LANGUAGES.map((l) => ({ id: l.code, label: l.nativeLabel }))}
      handleOptionSelect={(id) => {
        if (isSupportedLanguage(id)) dispatch(settings({ language: id }));
      }}
      ButtonText={current.nativeLabel}
      DropdownIcon={<Languages size={14} />}
      dropdownType='button'
      size={size}
      isVisibleIconShow={false}
      buttonProps={{
        variant: 'outline',
        colorScheme: 'gray',
        fontWeight: 'normal',
        isLoading: isPending,
        isDisabled: isPending
      }}
      ListStyle={{
        backgroundColor: surface(colors),
        borderRadius: '10px',
        border: `1px solid ${colors.boxborder[200]}`,
        overflow: 'hidden',
        minWidth: '140px'
      }}
      ItemStyle={{
        padding: '8px 14px',
        fontSize: '0.875rem',
        color: colors.text[900],
        cursor: 'pointer',
        backgroundColor: surface(colors)
      }}
      data-testid='language-select'
    />
  );
}
